import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Upload, message, Card, Select, Spin } from 'antd';
import { UploadOutlined, ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import CollectionService from '../api/CollectionService';
import AppInstance from '../api/GlobalAxios';

const { Option } = Select;
const { TextArea } = Input;

const CollectionForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [brands, setBrands] = useState([]);
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  // Fetch collection details if in edit mode
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setLoading(true);
        const response = await CollectionService.getAllBrands();
        
        if (response && response.data && Array.isArray(response.data)) {
          // Sort brands alphabetically by name for better UX
          const sortedBrands = [...response.data].sort((a, b) => {
            const nameA = a.name || a.brand_name || '';
            const nameB = b.name || b.brand_name || '';
            return nameA.localeCompare(nameB);
          });
          setBrands(sortedBrands);
        } else if (response && Array.isArray(response)) {
          // Handle case where response might be the array directly
          const sortedBrands = [...response].sort((a, b) => {
            const nameA = a.name || a.brand_name || '';
            const nameB = b.name || b.brand_name || '';
            return nameA.localeCompare(nameB);
          });
          setBrands(sortedBrands);
        } else {
          message.error('Failed to fetch brands: Invalid data format');
          // Set empty array to prevent UI issues
          setBrands([]);
        }
      } catch (error) {
        message.error('Failed to fetch brands: ' + (error.message || 'Unknown error'));
        // Set empty array to prevent UI issues
        setBrands([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();

    if (isEditMode) {
      fetchCollectionDetails();
    }
  }, [id, isEditMode]);

  const fetchCollectionDetails = async () => {
    try {
      setLoading(true);
      const response = await CollectionService.getCollectionById(id);
      
      if (response.statusCode === 200) {
        const collection = response.data;
        
        // Determine the brand ID correctly
        let brandId;
        if (collection.brand) {
          if (typeof collection.brand === 'object' && collection.brand._id) {
            brandId = collection.brand._id;
          } else if (typeof collection.brand === 'string') {
            brandId = collection.brand;
          }
        }
        
        form.setFieldsValue({
          name: collection.name,
          description: collection.description,
          meta_description: collection.meta_description,
          brand: brandId,
        });
        
        setImageUrl(collection.image);
      } else {
        message.error(response.message || 'Failed to fetch collection details');
      }
    } catch (error) {
      message.error('Failed to fetch collection details: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Helper function for debug logging
  const debugLog = (message, data = null) => {
    console.log(`[Collection Image Upload] ${message}`, data || '');
  };

  // Fallback manual upload method if needed
  const manualUpload = async (file) => {
    try {
      message.loading('Uploading via fallback method...', 0);
      debugLog('Starting manual upload method');
      
      const formData = new FormData();
      formData.append('productPictures', file);
      
      // Add metadata to help route the image correctly
      const collectionName = form.getFieldValue('name') || 'Unnamed Collection';
      const brandId = form.getFieldValue('brand');
      const selectedBrand = brandId ? brands.find(b => b._id === brandId) : null;
      const brandName = selectedBrand ? (selectedBrand.name || selectedBrand.brand_name) : '';
      
      if (brandName) {
        formData.append('brand', brandName);
      }
      
      if (collectionName) {
        formData.append('collection', collectionName);
      }
      
      // Try the product image upload endpoint with enhanced metadata
      debugLog('Attempting upload to /product/image/temp endpoint with metadata');
      let response;
      
      try {
        response = await AppInstance.post('/product/image/temp', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } catch (uploadError) {
        debugLog('Standard upload failed, trying alternative endpoint', uploadError.message);
        
        // Try the general media upload endpoint which might be more reliable
        try {
          formData.delete('productPictures');
          formData.append('file', file);
          response = await AppInstance.post('/media/upload', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        } catch (mediaError) {
          debugLog('Media upload also failed', mediaError.message);
          throw mediaError; // Re-throw to be caught by outer try/catch
        }
      }
      
      debugLog('Manual upload response received', response.data);
      
      // If we get a nested product response (most common)
      if (response.data && response.data.updatedProduct && 
          response.data.updatedProduct.productPictures && 
          Array.isArray(response.data.updatedProduct.productPictures) && 
          response.data.updatedProduct.productPictures.length > 0) {
        
        const imageUrl = response.data.updatedProduct.productPictures[0];
        setImageUrl(imageUrl);
        message.success('Image uploaded successfully');
        debugLog('Manual upload successful with image URL', imageUrl);
        
        // Update the collection directly if in edit mode
        if (isEditMode && id && imageUrl) {
          try {
            await CollectionService.updateCollection(id, { image: imageUrl });
            debugLog('Updated collection image directly after manual upload');
          } catch (updateErr) {
            debugLog('Failed to update collection directly, but image upload succeeded', updateErr.message);
          }
        }
        
        return true;
      }
      
      // Check for various response formats
      const possibleUrlFields = ['Location', 'data.Location', 'imageUrl', 'url', 'image', 'fileUrl'];
      let foundUrl = null;
      
      // Check each possible field path
      for (const field of possibleUrlFields) {
        const parts = field.split('.');
        let value = response.data;
        
        // Navigate through nested properties
        for (const part of parts) {
          if (value && typeof value === 'object' && part in value) {
            value = value[part];
          } else {
            value = null;
            break;
          }
        }
        
        // If we found a string URL, use it
        if (value && typeof value === 'string' && (value.startsWith('http') || value.startsWith('/'))) {
          foundUrl = value;
          break;
        }
      }
      
      // If we found a URL, set it
      if (foundUrl) {
        setImageUrl(foundUrl);
        message.success('Image uploaded successfully');
        debugLog('Manual upload successful with URL', foundUrl);
        
        // Update the collection directly if in edit mode
        if (isEditMode && id) {
          try {
            await CollectionService.updateCollection(id, { image: foundUrl });
            debugLog('Updated collection image directly after manual upload');
          } catch (updateErr) {
            debugLog('Failed to update collection directly, but image upload succeeded', updateErr.message);
          }
        }
        
        return true;
      }
      
      // Check for array of productPictures
      if (response.data && response.data.productPictures && 
          Array.isArray(response.data.productPictures) && 
          response.data.productPictures.length > 0) {
        
        setImageUrl(response.data.productPictures[0]);
        message.success('Image uploaded successfully');
        debugLog('Manual upload successful with productPictures array', response.data.productPictures[0]);
        
        // Update collection directly if in edit mode
        if (isEditMode && id) {
          try {
            await CollectionService.updateCollection(id, { image: response.data.productPictures[0] });
            debugLog('Updated collection image directly after upload');
          } catch (updateErr) {
            debugLog('Failed to update collection directly, but image upload succeeded', updateErr.message);
          }
        }
        
        return true;
      }
      
      // If response.data itself is a string URL
      if (response.data && typeof response.data === 'string' && 
          (response.data.startsWith('http') || response.data.startsWith('/'))) {
        
        setImageUrl(response.data);
        message.success('Image uploaded successfully');
        debugLog('Manual upload successful with direct string URL', response.data);
        
        // Update collection directly if in edit mode
        if (isEditMode && id) {
          try {
            await CollectionService.updateCollection(id, { image: response.data });
            debugLog('Updated collection image directly after upload');
          } catch (updateErr) {
            debugLog('Failed to update collection directly, but image upload succeeded', updateErr.message);
          }
        }
        
        return true;
      }
      
      debugLog('Manual upload failed - no usable URL in response', response.data);
      message.error('Manual upload failed - response format not recognized');
      return false;
    } catch (error) {
      console.error('Manual upload error:', error);
      debugLog('Manual upload error', error);
      message.error('Manual upload failed: ' + (error.message || 'Unknown error'));
      return false;
    } finally {
      message.destroy(); // Clear loading message
    }
  };

  // Last resort direct upload using the S3 mechanism from other components
  const lastResortUpload = async (file) => {
    try {
      message.loading('Trying alternative upload method...', 0);
      debugLog('Starting lastResortUpload method');
      
      // Create a form data object
      const formData = new FormData();
      formData.append('productPictures', file);
      
      // Try several endpoints that might work
      const endpoints = [
        { method: 'post', url: '/product/image/temp' },
        { method: 'post', url: '/product/image/placeholder' },
        { method: 'put', url: '/product/files/temp' }
      ];
      
      let uploaded = false;
      
      for (const endpoint of endpoints) {
        if (uploaded) break;
        
        try {
          debugLog(`Trying endpoint: ${endpoint.url} with method: ${endpoint.method}`);
          
          let response;
          if (endpoint.method === 'post') {
            response = await AppInstance.post(endpoint.url, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } else {
            response = await AppInstance.put(endpoint.url, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          }
          
          // Check various response formats
          debugLog('Upload response:', response.data);
          
          if (response.data) {
            // Check for product pictures array first (most likely format)
            if (response.data.updatedProduct && response.data.updatedProduct.productPictures && 
                Array.isArray(response.data.updatedProduct.productPictures) && 
                response.data.updatedProduct.productPictures.length > 0) {
              setImageUrl(response.data.updatedProduct.productPictures[0]);
              message.success('Image uploaded using alternative method');
              debugLog('Found URL in updatedProduct.productPictures', response.data.updatedProduct.productPictures[0]);
              uploaded = true;
              break;
            }
            
            // Check for direct productPictures array
            if (response.data.productPictures && Array.isArray(response.data.productPictures) && 
                response.data.productPictures.length > 0) {
              setImageUrl(response.data.productPictures[0]);
              message.success('Image uploaded using alternative method');
              debugLog('Found URL in productPictures', response.data.productPictures[0]);
              uploaded = true;
              break;
            }
            
            // Look for any field that might contain a URL
            const possibleUrlFields = ['url', 'image', 'imageUrl', 'Location', 'src', 'path'];
            
            for (const field of possibleUrlFields) {
              // Check in response.data
              if (response.data[field] && typeof response.data[field] === 'string') {
                setImageUrl(response.data[field]);
                message.success('Image uploaded using alternative method');
                uploaded = true;
                break;
              }
              
              // Check in response.data.data
              if (response.data.data && response.data.data[field] && 
                  typeof response.data.data[field] === 'string') {
                setImageUrl(response.data.data[field]);
                message.success('Image uploaded using alternative method');
                uploaded = true;
                break;
              }
            }
            
            // Check if data itself is a string URL
            if (!uploaded && typeof response.data === 'string' && 
                (response.data.startsWith('http') || response.data.startsWith('/'))) {
              setImageUrl(response.data);
              message.success('Image uploaded using alternative method');
              uploaded = true;
            }
          }
        } catch (err) {
          // Just try the next endpoint
          debugLog(`Endpoint ${endpoint.url} failed, trying next...`, err.message);
        }
      }
      
      message.destroy(); // Clear loading message
      return uploaded;
    } catch (error) {
      message.destroy();
      console.error('All upload attempts failed:', error);
      message.error('All upload attempts failed');
      return false;
    }
  };

  // Direct S3 upload method as final fallback
  const directS3Upload = async (file) => {
    try {
      message.loading('Creating temporary product for image storage...', 0);
      debugLog('Starting directS3Upload method - temporary product approach');
      
      // Get the current form values to extract brand info
      const formValues = form.getFieldsValue();
      const brandId = formValues.brand; // Get the selected brand ID from form
      
      // Find brand details if a brand is selected
      const selectedBrand = brands.find(b => b._id === brandId);
      const brandName = selectedBrand ? (selectedBrand.name || selectedBrand.brand_name) : 'Default Brand';
      
      debugLog('Form values for product creation:', { brandId, brandName });
      
      // Ensure we have valid brand information and handle ObjectId validation
      // MongoDB ObjectId is 24 hex characters, so ensure the brandId follows that pattern
      const validBrandId = brandId && /^[0-9a-fA-F]{24}$/.test(brandId) 
        ? brandId 
        : '64516e1cc9ba83a27e61a111'; // Use a default brand ID if none or invalid
      
      // Create a temporary product to attach the image to with all required fields
      const collectionName = form.getFieldValue('name') || 'Temporary Collection';
      const timestamp = Date.now();
      
      // Add a clear marker that this is a temporary product for image storage
      const tempProductName = `[TEMP] ${collectionName} - Collection Image Storage (${timestamp})`;
      const tempProduct = {
        name: tempProductName,
        slug: `temp-collection-image-${timestamp}`,
        price: 999,
        sale_price: 999,
        sku: `temp-collection-image-${timestamp}`,
        stock: 1,
        // Required brand information
        BrandID: validBrandId,
        BrandName: brandName || 'Default Brand',
        brand: validBrandId, // Include both formats as the API might expect either
        // Required category information - must match existing categories in the system
        CatID: '64516e1dc9ba83a27e61a333', 
        CatName: 'Collections',
        SubCatID: '64516e1dc9ba83a27e61a444',
        SubCatName: 'Collection Images',
        SuperCatID: '64516e1dc9ba83a27e61a555',
        SuperCatName: 'Collections',
        // Required product information matching exactly what ProductCtl.js expects
        short_desc: `Temporary product for ${collectionName} image upload - WILL BE DELETED`,
        long_desc: `This is a temporary product created for ${collectionName} image storage only. This product will be deleted automatically.`,
        // Set the actual collection name to ensure proper association
        Collection_Name: collectionName,
        productPictures: [], // Initialize with empty array to be filled later
        // Additional required fields based on Product model
        quantity: 1,
        category: '64516e1dc9ba83a27e61a333', // Same as CatID
        // Add enhanced metadata to make it easier to clean up these temp products later
        is_temporary: true, 
        temp_purpose: 'collection_image_upload',
        temp_created_at: new Date().toISOString(),
        temp_created_by: 'collection_form', 
        temp_collection_name: collectionName,
        temp_collection_id: id || 'new_collection',
        created_at: new Date().toISOString(),
      };
      
      // Create temp product with enhanced error handling
      debugLog('Creating temporary product with brand info', tempProduct);
      let productResponse;
      
      try {
        productResponse = await AppInstance.post('/product/create', tempProduct);
        
        if (!productResponse || !productResponse.data || !productResponse.data._id) {
          message.destroy();
          const errorMsg = productResponse?.data?.error || 'Failed to create temporary storage for image';
          message.error(errorMsg);
          debugLog('Failed to create temporary product', productResponse?.data);
          return false;
        }
      } catch (createError) {
        message.destroy();
        debugLog('Error creating temporary product (full error):', createError);
        
        // Try to extract more detailed error information
        const responseData = createError?.response?.data;
        const errorMsg = responseData?.error || responseData?.message || createError.message || 'Unknown error creating temporary product';
        message.error(errorMsg);
        
        // Log more details for debugging
        if (responseData) {
          debugLog('Server response data:', responseData);
        }
        
        debugLog('Error creating temporary product:', errorMsg);
        return false;
      }
      
      const tempProductId = productResponse.data._id;
      debugLog('Created temp product with ID:', tempProductId);
      
      // Upload image to the temp product
      const formData = new FormData();
      formData.append('productPictures', file);
      
      debugLog('Uploading image to temporary product');
      try {
        const uploadResponse = await AppInstance.post(
          `/product/image/${tempProductId}`, 
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        debugLog('Upload response from direct S3 upload:', uploadResponse.data);
        
        if (uploadResponse.data && 
            uploadResponse.data.updatedProduct && 
            uploadResponse.data.updatedProduct.productPictures && 
            uploadResponse.data.updatedProduct.productPictures.length > 0) {
          
          // Get the image URL
          const imageUrl = uploadResponse.data.updatedProduct.productPictures[0];
          setImageUrl(imageUrl);
          
          message.destroy();
          message.success('Image uploaded successfully via temporary storage');
          debugLog('Temporary product image upload successful with URL:', imageUrl);
          
          // If we're in edit mode and have a collection ID, try to update the collection image directly
          if (isEditMode && id) {
            try {
              debugLog('Updating collection image directly');
              await CollectionService.updateCollection(id, { image: imageUrl });
              debugLog('Collection image updated directly');
            } catch (updateErr) {
              debugLog('Failed to update collection image directly, but image upload succeeded', updateErr.message);
            }
          }
          
          // Delete the temporary product
          try {
            debugLog('Cleaning up - deleting temporary product');
            message.loading('Cleaning up temporary storage...', 0);
            await AppInstance.delete(`/product/delete/${tempProductId}`);
            debugLog('Temporary product deleted successfully');
            message.destroy();
            message.success('Image uploaded and temporary storage cleaned up successfully');
            
            // Log successful cleanup for tracking
            console.log(`Successfully cleaned up temporary product: ${tempProductId}`);
          } catch (deleteErr) {
            debugLog('Failed to delete temporary product, but upload succeeded', deleteErr.message);
            message.destroy();
            message.warning('Image uploaded successfully, but failed to clean up temporary storage. This will not affect your collection.');
            
            // Log this error to help with manual cleanup later
            console.error('Failed to delete temporary product:', {
              tempProductId,
              productName: tempProductName,
              error: deleteErr.message,
              timestamp: new Date().toISOString()
            });
            
            // Schedule a retry of the cleanup after a short delay
            setTimeout(async () => {
              try {
                await AppInstance.delete(`/product/delete/${tempProductId}`);
                console.log(`Delayed cleanup of temporary product ${tempProductId} succeeded`);
              } catch (retryErr) {
                console.error(`Delayed cleanup failed for product ${tempProductId}:`, retryErr.message);
              }
            }, 5000);
          }
          
          return true;
        }
        
        debugLog('Direct upload failed - no usable URL in response', uploadResponse.data);
        message.destroy();
        message.error('Direct upload failed - unexpected response format');
        return false;
      } catch (uploadError) {
        debugLog('Image upload error:', uploadError.message, uploadError?.response?.data);
        message.destroy();
        message.error('Image upload failed: ' + (uploadError?.response?.data?.error || uploadError.message));
        return false;
      }
    } catch (error) {
      debugLog('Direct S3 upload error:', error.message);
      console.error('Direct S3 upload error:', error);
      message.destroy();
      message.error('Direct upload failed: ' + (error.message || 'Unknown error'));
      return false;
    }
  };

  // Utility function to test if an endpoint is working
  const testEndpoint = async (endpoint, method = 'get', data = null) => {
    try {
      debugLog(`Testing endpoint ${endpoint} with method ${method}`);
      let response;
      
      if (method.toLowerCase() === 'get') {
        response = await AppInstance.get(endpoint);
      } else if (method.toLowerCase() === 'post') {
        response = await AppInstance.post(endpoint, data || {});
      } else if (method.toLowerCase() === 'put') {
        response = await AppInstance.put(endpoint, data || {});
      }
      
      debugLog(`Test endpoint ${endpoint} response:`, response.data);
      return { success: true, data: response.data, status: response.status };
    } catch (error) {
      const status = error.response ? error.response.status : 'No status';
      const responseData = error.response ? error.response.data : null;
      
      debugLog(`Test endpoint ${endpoint} error:`, { 
        message: error.message, 
        status, 
        data: responseData 
      });
      
      return { 
        success: false, 
        error: error.message, 
        status,
        data: responseData
      };
    }
  };

  // Test available endpoints for development purposes
  const testAllUploadEndpoints = async () => {
    if (process.env.NODE_ENV !== 'development') {
      message.info('This functionality is only available in development mode');
      return;
    }
    
    message.loading('Testing API endpoints...', 0);
    
    const endpoints = [
      { url: '/product/image/temp', method: 'get' },
      { url: '/product/imageoptimization', method: 'get' },
      { url: '/media/upload', method: 'get' },
      { url: '/uploads/product', method: 'get' },
      { url: '/brand/getAll', method: 'get' },
      { url: '/collections', method: 'get' }
    ];
    
    const results = {};
    
    for (const endpoint of endpoints) {
      results[endpoint.url] = await testEndpoint(endpoint.url, endpoint.method);
    }
    
    // Test if we have a valid brand ID
    const brandId = form.getFieldValue('brand');
    if (brandId) {
      results[`/brand/get/${brandId}`] = await testEndpoint(`/brand/get/${brandId}`, 'get');
    }
    
    // Test if we have a valid collection ID in edit mode
    if (isEditMode && id) {
      results[`/collections/id/${id}`] = await testEndpoint(`/collections/id/${id}`, 'get');
    }
    
    message.destroy();
    message.success('Endpoint tests completed. Check console for results.');
    console.log('🧪 API Endpoint Test Results:', results);
  };
  
  // Specialized method for direct collection image upload
  const directCollectionImageUpload = async (file) => {
    if (!file) return false;
    
    try {
      message.loading('Uploading collection image...', 0);
      debugLog('Starting directCollectionImageUpload method');
      
      // Use the new dedicated collection image upload endpoints
      const formData = new FormData();
      formData.append('image', file);
      
      let uploadResponse;
      
      // If we're in edit mode, use the collection-specific endpoint
      if (isEditMode && id) {
        debugLog('Using collection-specific image upload endpoint');
        uploadResponse = await AppInstance.post(`/collections/${id}/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        // Otherwise use the temporary image endpoint
        debugLog('Using temporary collection image upload endpoint');
        uploadResponse = await AppInstance.post('/collections/image/temp', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      debugLog('Collection image upload response:', uploadResponse.data);
      
      // Check if we have a successful response with imageUrl
      if (uploadResponse.data && uploadResponse.data.data && uploadResponse.data.data.imageUrl) {
        const imageUrl = uploadResponse.data.data.imageUrl;
        setImageUrl(imageUrl);
        
        message.destroy();
        message.success('Collection image uploaded successfully');
        
        // Update the collection with the new image if in edit mode
        if (isEditMode && id) {
          try {
            await CollectionService.updateCollection(id, { image: imageUrl });
            debugLog('Updated collection image successfully');
          } catch (updateErr) {
            debugLog('Failed to update collection, but image upload succeeded', updateErr.message);
          }
        }
        
        return true;
      }
      
      // Fallback to old approach if response is in a different format
      if (uploadResponse.data) {
        let imageUrl = null;
        
        // Try various response formats
        if (typeof uploadResponse.data === 'string' && 
            (uploadResponse.data.startsWith('http') || uploadResponse.data.startsWith('/'))) {
          imageUrl = uploadResponse.data;
        } else if (uploadResponse.data.image) {
          imageUrl = uploadResponse.data.image;
        } else if (uploadResponse.data.url) {
          imageUrl = uploadResponse.data.url;
        } else if (uploadResponse.data.data && 
            (uploadResponse.data.data.image || uploadResponse.data.data.url || uploadResponse.data.data.Location)) {
          imageUrl = uploadResponse.data.data.image || uploadResponse.data.data.url || uploadResponse.data.data.Location;
        }
        
        if (imageUrl) {
          setImageUrl(imageUrl);
          message.destroy();
          message.success('Collection image uploaded successfully');
          
          // Update the collection with the new image if in edit mode
          if (isEditMode && id) {
            await CollectionService.updateCollection(id, { image: imageUrl });
          }
          
          return true;
        }
      }
      
      // Try direct media upload response formats
      debugLog('Media upload response:', uploadResponse.data);
      
      if (uploadResponse.data && (uploadResponse.data.url || uploadResponse.data.fileUrl || uploadResponse.data.image || uploadResponse.data.Location)) {
        const imageUrl = uploadResponse.data.url || uploadResponse.data.fileUrl || uploadResponse.data.image || uploadResponse.data.Location;
        setImageUrl(imageUrl);
        
        message.destroy();
        message.success('Collection image uploaded successfully');
        
        // Update the collection with the new image if in edit mode
        if (isEditMode && id) {
          await CollectionService.updateCollection(id, { image: imageUrl });
          debugLog('Updated collection image directly');
        }
        
        return true;
      }
    } catch (directUploadError) {
        debugLog('Direct media upload failed:', directUploadError.message);
        // Continue to next attempt - don't return yet
    }
      
    // Next, try sending directly to collection image endpoint if it exists
    try {
      const collectionFormData = new FormData();
      collectionFormData.append('image', file);
      
      // If we're in edit mode, try the direct collection image upload endpoint
      if (isEditMode && id) {
        debugLog('Attempting direct upload to collection image endpoint');
        const collectionUploadResponse = await AppInstance.post(
          `/collections/${id}/image`, 
          collectionFormData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        debugLog('Collection image upload response:', collectionUploadResponse.data);
        
        // Check if we got a valid response with an image URL
        if (collectionUploadResponse.data) {
          let imageUrl = null;
          
          // Try various response formats
          if (typeof collectionUploadResponse.data === 'string' && 
              (collectionUploadResponse.data.startsWith('http') || collectionUploadResponse.data.startsWith('/'))) {
            imageUrl = collectionUploadResponse.data;
          } else if (collectionUploadResponse.data.image) {
            imageUrl = collectionUploadResponse.data.image;
          } else if (collectionUploadResponse.data.url) {
            imageUrl = collectionUploadResponse.data.url;
          } else if (collectionUploadResponse.data.data && 
                    (collectionUploadResponse.data.data.image || collectionUploadResponse.data.data.url || 
                      collectionUploadResponse.data.data.Location)) {
            imageUrl = collectionUploadResponse.data.data.image || 
                      collectionUploadResponse.data.data.url || 
                      collectionUploadResponse.data.data.Location;
          }
          
          if (imageUrl) {
            setImageUrl(imageUrl);
            message.destroy();
            message.success('Collection image uploaded successfully');
            return true;
          }
        }
      }
    } catch (collectionUploadError) {
      debugLog('Collection specific upload failed:', collectionUploadError.message);
      // Continue to next attempt - don't return yet
    }
      
    // Try using the image optimization endpoint as a direct upload method
    try {
      debugLog('Attempting image optimization endpoint upload');
      const optimizeFormData = new FormData();
      optimizeFormData.append('productPictures', file);
      
      const optimizeResponse = await AppInstance.post(
        '/product/imageoptimization',
        optimizeFormData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      debugLog('Image optimization response:', optimizeResponse.data);
      
      if (optimizeResponse.data && typeof optimizeResponse.data === 'string' &&
          optimizeResponse.data.startsWith('http')) {
          
        const imageUrl = optimizeResponse.data;
        setImageUrl(imageUrl);
        message.destroy();
        message.success('Collection image uploaded successfully via optimization service');
        
        // Update the collection with the new image if in edit mode
        if (isEditMode && id) {
          await CollectionService.updateCollection(id, { image: imageUrl });
          debugLog('Updated collection image directly after optimization upload');
        }
        
        return true;
      }
    } catch (optimizeError) {
      debugLog('Image optimization upload failed:', optimizeError.message);
      // Fall through to next method
    }
    
    // Try a direct upload to the product image endpoint without product ID
    try {
      debugLog('Attempting direct product image upload (no product ID)');
      const directFormData = new FormData();
      directFormData.append('productPictures', file);
      
      // Add collection and brand context to help server-side routing
      const formValues = form.getFieldsValue();
      const collectionName = formValues.name || 'unnamed-collection';
      const brandId = formValues.brand;
      const brandObj = brandId ? brands.find(b => b._id === brandId) : null;
      const brandName = brandObj ? (brandObj.name || brandObj.brand_name || '') : '';
      
      if (brandName) {
        directFormData.append('brand', brandName);
        directFormData.append('brandId', brandId);
      }
      
      directFormData.append('purpose', 'collection');
      directFormData.append('collectionName', collectionName);
      
      const directResponse = await AppInstance.post(
        '/product/uploadimage',
        directFormData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      debugLog('Direct product image upload response:', directResponse.data);
      
      // Check for URL in various response formats
      if (directResponse.data) {
        let imageUrl = null;
        
        if (typeof directResponse.data === 'string' && directResponse.data.startsWith('http')) {
          imageUrl = directResponse.data;
        } else if (directResponse.data.url) {
          imageUrl = directResponse.data.url;
        } else if (directResponse.data.Location) {
          imageUrl = directResponse.data.Location;
        } else if (directResponse.data.image) {
          imageUrl = directResponse.data.image;
        } else if (directResponse.data.data && 
                  (directResponse.data.data.url || directResponse.data.data.Location || directResponse.data.data.image)) {
          imageUrl = directResponse.data.data.url || directResponse.data.data.Location || directResponse.data.data.image;
        }
        
        if (imageUrl) {
          setImageUrl(imageUrl);
          message.destroy();
          message.success('Collection image uploaded successfully');
          
          // Update the collection with the new image if in edit mode
          if (isEditMode && id) {
            await CollectionService.updateCollection(id, { image: imageUrl });
            debugLog('Updated collection image directly after direct upload');
          }
          
          return true;
        }
      }
    } catch (directError) {
      debugLog('Direct product image upload failed:', directError.message);
      // Fall through to product-based upload
    }
    
    // If all direct methods fail, fall back to the product-based S3 upload method
    // This is a last resort method that creates temporary products
    message.destroy();
    message.warning('Direct upload methods failed. The system needs to use a fallback method.');
    debugLog('⚠️ All direct methods failed. Temporary product creation may be required.');
    
    // Provide very clear information to the user about what's happening
    const confirmMessage = `
All direct upload methods failed. 

The system needs to use a fallback method that will:
1. Create a temporary product marked with "[TEMP]"
2. Upload the image to this temporary product
3. Extract the image URL for the collection
4. Delete the temporary product

This approach is less efficient but should resolve the upload issue.

Would you like to proceed with this fallback method?
(Cancel to abort upload)
    `;
    
    if (window.confirm(confirmMessage)) {
      // Show a more detailed message to the user
      message.loading('Creating temporary storage for your image. This will be automatically cleaned up...', 0);
      const result = await directS3Upload(file);
      message.destroy();
      return result;
    } else {
      message.info('Upload canceled. Please try again with a different image or contact support.');
      return false;
    }
  };

  // Utility function to help debug upload issues
  const testImageEndpoint = async () => {
    try {
      message.loading('Testing image upload methods...');
      
      // Create a basic file blob for testing
      const base64Data = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==';
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      const testFile = new File([byteArray], 'test.png', { type: 'image/png' });
      
      // Get current form values for context
      const formValues = form.getFieldsValue();
      debugLog('Current form values for testing:', formValues);
      
      // Gather information about the current state
      const brandId = formValues.brand;
      const brandObj = brandId ? brands.find(b => b._id === brandId) : null;
      const collectionName = formValues.name || 'TestCollection';
      
      // Create status object to track results
      const testResults = {
        context: {
          formValues,
          brandInfo: brandObj ? {
            id: brandObj._id,
            name: brandObj.name || brandObj.brand_name,
          } : 'No brand selected',
          collectionName,
          isEditMode,
          id: id || null,
        },
        methods: {},
      };
      
      // Test upload methods
      debugLog('Starting upload method tests with test image');
      
      // Test collection-specific endpoint first
      try {
        debugLog('Testing collection image upload directly');
        const start = Date.now();
        const collectionTestResult = await directCollectionImageUpload(testFile);
        const end = Date.now();
        
        testResults.methods.directCollectionImageUpload = {
          success: collectionTestResult,
          time: `${end - start}ms`,
        };
        
        if (collectionTestResult) {
          debugLog('Collection image upload test succeeded');
          message.destroy();
          message.success('Direct collection upload test succeeded');
          return 'directCollectionImageUpload succeeded';
        }
      } catch (collErr) {
        debugLog('Collection upload method test failed:', collErr);
        testResults.methods.directCollectionImageUpload = {
          success: false,
          error: collErr.message,
        };
      }
      
      // Try manual upload
      try {
        debugLog('Testing manual upload method');
        const start = Date.now();
        const manualResult = await manualUpload(testFile);
        const end = Date.now();
        
        testResults.methods.manualUpload = {
          success: manualResult,
          time: `${end - start}ms`,
        };
        
        if (manualResult) {
          debugLog('Manual upload test succeeded');
          message.destroy();
          message.success('Manual upload test succeeded');
          return 'manualUpload succeeded';
        }
      } catch (manualErr) {
        debugLog('Manual upload test failed:', manualErr);
        testResults.methods.manualUpload = {
          success: false,
          error: manualErr.message,
        };
      }
      
      // Try lastResortUpload
      try {
        debugLog('Testing last resort upload method');
        const start = Date.now();
        const lastResortResult = await lastResortUpload(testFile);
        const end = Date.now();
        
        testResults.methods.lastResortUpload = {
          success: lastResortResult,
          time: `${end - start}ms`,
        };
        
        if (lastResortResult) {
          debugLog('Last resort upload test succeeded');
          message.destroy();
          message.success('Last resort upload test succeeded');
          return 'lastResortUpload succeeded';
        }
      } catch (lastResortErr) {
        debugLog('Last resort upload test failed:', lastResortErr);
        testResults.methods.lastResortUpload = {
          success: false,
          error: lastResortErr.message,
        };
      }
      
      // Try directS3Upload
      try {
        debugLog('Testing direct S3 upload method');
        const start = Date.now();
        const s3Result = await directS3Upload(testFile);
        const end = Date.now();
        
        testResults.methods.directS3Upload = {
          success: s3Result,
          time: `${end - start}ms`,
        };
        
        if (s3Result) {
          debugLog('Direct S3 upload test succeeded');
          message.destroy();
          message.success('Direct S3 upload test succeeded');
          return 'directS3Upload succeeded';
        }
      } catch (s3Err) {
        debugLog('Direct S3 upload test failed:', s3Err);
        testResults.methods.directS3Upload = {
          success: false,
          error: s3Err.message,
        };
      }
      
      // Log comprehensive test results
      console.log('📊 Image Upload Test Results:', testResults);
      
      message.destroy();
      message.error('All upload test methods failed');
      return 'All methods failed';
    } catch (error) {
      debugLog('Test failed with error', error);
      message.destroy();
      message.error('Upload testing error: ' + error.message);
      return 'Test error: ' + error.message;
    }
  };

  const handleFinish = async (values) => {
    try {
      setLoading(true);
      debugLog('Form submission started with values:', values);
      debugLog('Current image URL:', imageUrl);
      
      // Prepare data for API call - include brand field explicitly
      const data = {
        name: values.name,
        description: values.description || "",
        meta_description: values.meta_description || "",
        brand: values.brand || null, // Explicitly set brand as null if not provided
        image: imageUrl || "",
      };
      
      debugLog('Sending collection data to API:', data);

      let response;
      if (isEditMode) {
        response = await CollectionService.updateCollection(id, data);
      } else {
        response = await CollectionService.createCollection(data);
      }
      
      debugLog('API response:', response);

      if (response.statusCode === 200 || response.statusCode === 201) {
        message.success(`Collection ${isEditMode ? 'updated' : 'created'} successfully`);
        navigate('/admin/collections');
      } else {
        throw new Error(response.message || 'Operation failed');
      }
    } catch (error) {
      message.error(`Failed to ${isEditMode ? 'update' : 'create'} collection: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (info) => {
    if (info.file.status === 'uploading') {
      message.loading('Uploading...');
      return;
    }
    
    if (info.file.status === 'done') {
      // Handle different response formats
      debugLog('Upload response received:', info.file.response);
      
      // Try to extract URL from response using various formats
      let imageUrl = null;
      const response = info.file.response;
      
      if (response) {
        // Collection image controller response format (prioritize this)
        if (response.data && response.data.imageUrl) {
          imageUrl = response.data.imageUrl;
          debugLog('Found imageUrl in new collection image response format', imageUrl);
        } 
        // Collection image response format (alternative)
        else if (response.data && response.data.collection && response.data.collection.image) {
          imageUrl = response.data.collection.image;
          debugLog('Found image in collection object', imageUrl);
        }
        
        if (response.fileUrl) {
          imageUrl = response.fileUrl;
        } else if (response.data) {
          // Check nested data object for URL fields
          const data = response.data;
          if (data.url) {
            imageUrl = data.url;
          } else if (data.image) {
            imageUrl = data.image;
          } else if (data.fileUrl) {
            imageUrl = data.fileUrl;
          } else if (typeof data === 'string' && (data.startsWith('http') || data.startsWith('/'))) {
            imageUrl = data;
          }
        } else if (typeof response === 'string' && (response.startsWith('http') || response.startsWith('/'))) {
          // Direct string URL response
          imageUrl = response;
        }
        
        // Check for nested updatedProduct structure
        if (!imageUrl && response.updatedProduct && response.updatedProduct.productPictures && 
            Array.isArray(response.updatedProduct.productPictures) && 
            response.updatedProduct.productPictures.length > 0) {
          imageUrl = response.updatedProduct.productPictures[0];
        }
        
        // Check for direct array of productPictures
        if (!imageUrl && response.productPictures && 
            Array.isArray(response.productPictures) && 
            response.productPictures.length > 0) {
          imageUrl = response.productPictures[0];
        }
      }
      
      // If we found a URL, set it and update the collection if in edit mode
      if (imageUrl) {
        setImageUrl(imageUrl);
        message.success('Image uploaded successfully');
        debugLog('Standard upload successful with image URL', imageUrl);
        
        // Update collection directly if in edit mode
        if (isEditMode && id) {
          try {
            await CollectionService.updateCollection(id, { image: imageUrl });
            debugLog('Updated collection image directly after standard upload');
          } catch (updateErr) {
            debugLog('Failed to update collection directly, but image upload succeeded', updateErr.message);
          }
        }
        
        return;
      }
      
      // If we get here, none of the response formats matched
      debugLog('Standard upload failed with unexpected response format:', info.file.response);
      
      // Try our fallback methods in sequence
      if (info.file.originFileObj) {
        message.info('Trying alternative upload methods...');
        
        // First try our specialized collection image upload method
        let success = await directCollectionImageUpload(info.file.originFileObj);
        
        // If that fails, try manual upload
        if (!success) {
          debugLog('Collection upload failed, trying manualUpload');
          success = await manualUpload(info.file.originFileObj);
        }
        
        // If that fails, try lastResortUpload
        if (!success) {
          debugLog('Manual upload failed, trying lastResortUpload');
          success = await lastResortUpload(info.file.originFileObj);
        }
        
        // Only use directS3Upload (which creates temp products) as absolute last resort
        if (!success) {
          debugLog('All direct methods failed. Consider adding a direct image endpoint instead of using temporary products.');
          message.warning('Direct upload methods failed - fallback required');
          
          // Confirm with user before using method that creates temporary products
          const confirmMessage = `
All direct upload methods failed. 

The system needs to use a fallback method that will:
1. Create a temporary product marked with "[TEMP]"
2. Upload the image to this temporary product
3. Extract the image URL for the collection
4. Delete the temporary product

This approach is less efficient but should resolve the upload issue.

Would you like to proceed with this fallback method?
(Cancel to abort upload)
          `;
          
          if (window.confirm(confirmMessage)) {
            message.loading('Creating temporary storage for your image...', 0);
            success = await directS3Upload(info.file.originFileObj);
            if (!success) {
              message.destroy();
              message.error('All upload methods failed. Please try a different image or contact support.');
            }
          } else {
            message.info('Upload canceled. Please try again with a different image or contact support.');
          }
        }
        
        if (!success) {
          message.error('All upload methods failed. Please try again or contact support.');
        }
      }
    } else if (info.file.status === 'error') {
      message.error('Image upload failed: ' + (info.file.error?.message || 'Unknown error'));
      
      // Try multiple fallback methods in sequence
      if (info.file.originFileObj) {
        message.info('Trying alternative upload methods due to error...');
        
        // First try our specialized collection upload method
        let success = await directCollectionImageUpload(info.file.originFileObj);
        
        if (!success) {
          success = await manualUpload(info.file.originFileObj);
        }
        
        if (!success) {
          success = await lastResortUpload(info.file.originFileObj);
        }
        
        if (!success) {
          // Final attempt - direct S3 upload
          message.warning('Direct upload methods failed - fallback required');
          
          // Provide detailed information to the user
          const confirmMessage = `
All direct upload methods failed. 

The system needs to use a fallback method that will:
1. Create a temporary product marked with "[TEMP]"
2. Upload the image to this temporary product
3. Extract the image URL for the collection
4. Delete the temporary product

This approach is less efficient but should resolve the upload issue.

Would you like to proceed with this fallback method?
(Cancel to abort upload)
          `;
          
          if (window.confirm(confirmMessage)) {
            message.loading('Creating temporary storage for your image...', 0);
            success = await directS3Upload(info.file.originFileObj);
            message.destroy();
            if (!success) {
              message.error('All upload methods failed. Please try a different image or contact support.');
            }
          } else {
            message.info('Upload canceled. Please try again with a different image.');
            return;
          }
        }
        
        if (!success) {
          message.error('All upload methods failed, please try a different image or contact support.');
        }
      }
    }
  };

  return (
    <div className="p-4">
      <style jsx="true">{`
        .avatar-uploader .ant-upload-list-item-info {
          border-radius: 10px;
          overflow: hidden;
        }
        
        .image-hover-overlay:hover {
          opacity: 1 !important;
        }
      `}</style>
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">
          {isEditMode ? 'Edit Collection' : 'Create Collection'}
        </h1>
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/collections')}
          style={{ borderRadius: "10px" }}
        >
          Back to Collections
        </Button>
      </div>

      <Card>
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            initialValues={{
              name: '',
              description: '',
              meta_description: '',
              brand: undefined,
            }}
          >
            <Form.Item
              name="name"
              label="Collection Name"
              rules={[{ required: true, message: 'Please enter collection name' }]}
            >
              <Input placeholder="Enter collection name" />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
            >
              <TextArea rows={4} placeholder="Enter collection description" />
            </Form.Item>

            <Form.Item
              name="meta_description"
              label="Meta Description"
            >
              <TextArea rows={2} placeholder="Enter meta description for SEO" />
            </Form.Item>

            <Form.Item
              name="brand"
              label="Brand"
            >
              <Select 
                placeholder="Select a brand" 
                allowClear
                loading={loading && brands.length === 0}
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
                filterOption={(input, option) => 
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
              >
                {brands && brands.length > 0 ? (
                  brands.map((brand) => (
                    <Option key={brand._id} value={brand._id}>
                      {brand.name || brand.brand_name}
                    </Option>
                  ))
                ) : (
                  <Option disabled>Loading brands...</Option>
                )}
              </Select>
              <small>Associate this collection with a brand</small>
            </Form.Item>

            <Form.Item label="Collection Image">
              <div className="flex flex-col">
                <Upload
                  name="image"
                  listType="picture-card"
                  className="avatar-uploader"
                  showUploadList={false}
                  action={`${process.env.REACT_APP_API_URL || 'https://flooringdeals.ca/api'}/collections/${isEditMode && id ? `${id}/image` : 'image/temp'}`}
                  accept="image/png, image/jpeg, image/jpg, image/webp"
                  onChange={handleImageUpload}
                  beforeUpload={(file) => {
                    // Validate the file before upload
                    const isImage = file.type.startsWith('image/');
                    if (!isImage) {
                      message.error('You can only upload image files!');
                      return false;
                    }
                    
                    const isLt2M = file.size / 1024 / 1024 < 2;
                    if (!isLt2M) {
                      message.error('Image must be smaller than 2MB!');
                      return false;
                    }
                    
                    // Check if collection name is filled out (strongly recommended for better organization)
                    const collectionName = form.getFieldValue('name');
                    if (!collectionName) {
                      message.warning('It is recommended to enter a collection name before uploading an image for better organization.');
                    }
                    
                    return true;
                  }}
                  onError={(error) => {
                    console.error("Upload error:", error);
                    // Error will be handled by onChange with status='error'
                  }}
                  customRequest={async ({ file, onSuccess, onError }) => {
                    // Implement custom upload handler to use our optimized upload method directly
                    try {
                      // Start with our most reliable method
                      let success = await directCollectionImageUpload(file);
                      
                      if (success) {
                        // Call onSuccess to trigger the onChange handler
                        onSuccess({ status: 'done' });
                      } else {
                        // Let the upload component handle the fallback chain
                        onError(new Error('Primary upload method failed'));
                      }
                    } catch (error) {
                      console.error("Custom upload error:", error);
                      onError(error);
                    }
                  }}
                >
                  {imageUrl ? (
                    <div style={{ position: 'relative' }}>
                      <img src={imageUrl} alt="Collection" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <div className="image-hover-overlay" style={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        right: 0, 
                        bottom: 0, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        backgroundColor: 'rgba(0,0,0,0.5)', 
                        opacity: 0, 
                        transition: 'opacity 0.3s',
                        cursor: 'pointer'
                      }}>
                        <UploadOutlined style={{ fontSize: '24px', color: 'white' }} />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <UploadOutlined style={{ fontSize: '24px' }} />
                      <div style={{ marginTop: 8 }}>Upload</div>
                    </div>
                  )}
                </Upload>
                <small className="mt-2">Upload a representative image for this collection (Max size: 2MB)</small>
                {!isEditMode && !imageUrl && (
                  <small className="block text-gray-500 mt-1">
                    <strong>Tip:</strong> Fill in the collection name and select a brand before uploading for best results
                  </small>
                )}
                {isEditMode && !imageUrl && (
                  <small className="block text-gray-500 mt-1">
                    <strong>Note:</strong> Uploading a new image will replace the existing one
                  </small>
                )}
                
                {/* Fallback manual upload button for better user experience */}
                {!imageUrl && (
                  <Button 
                    className="mt-3"
                    style={{ width: "fit-content" }}
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/png, image/jpeg, image/jpg, image/webp';
                      input.onchange = async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          // Validate the file
                          const isImage = file.type.startsWith('image/');
                          if (!isImage) {
                            message.error('You can only upload image files!');
                            return;
                          }
                          
                          const isLt2M = file.size / 1024 / 1024 < 2;
                          if (!isLt2M) {
                            message.error('Image must be smaller than 2MB!');
                            return;
                          }
                          
                          // Check if collection name is filled out
                          const collectionName = form.getFieldValue('name');
                          if (!collectionName) {
                            message.warning('It is recommended to enter a collection name before uploading an image for better organization.');
                          }
                          
                          // Start with our most reliable method
                          let success = await directCollectionImageUpload(file);
                          
                          // Try fallbacks if needed
                          if (!success) {
                            success = await manualUpload(file);
                          }
                          
                          if (!success) {
                            success = await lastResortUpload(file);
                          }
                          
                          // Only use temp product creation as absolute last resort
                          if (!success) {
                            message.warning('Using legacy upload method (temporary product creation) as last resort');
                            if (window.confirm('All direct upload methods failed. Use legacy method that creates temporary products?')) {
                              success = await directS3Upload(file);
                            } else {
                              message.info('Upload canceled. Please try again with a different image.');
                              return;
                            }
                          }
                          
                          if (!success) {
                            message.error('All upload methods failed, please try a different image or contact support.');
                          }
                        }
                      };
                      input.click();
                    }}
                  >
                    Alternative Upload Method
                  </Button>
                )}

                {/* Enhanced debugging tools - only show in development */}
                {process.env.NODE_ENV === 'development' && (
                  <div className="mt-3 border-t pt-2">
                    <p className="text-xs text-gray-500 mb-1">Developer Tools:</p>
                    <Button 
                      size="small" 
                      type="link"
                      style={{ fontSize: '11px', padding: 0 }}
                      onClick={async () => {
                        const result = await testImageEndpoint();
                        message.info(`Test result: ${result}`);
                      }}
                    >
                      Test Upload Methods
                    </Button>
                    <Button 
                      size="small" 
                      type="link"
                      style={{ fontSize: '11px', padding: 0, marginLeft: '10px' }}
                      onClick={testAllUploadEndpoints}
                    >
                      Test API Endpoints
                    </Button>
                    <Button
                      size="small"
                      type="link"
                      style={{ fontSize: '11px', padding: 0, marginLeft: '10px' }}
                      onClick={() => {
                        const formValues = form.getFieldsValue();
                        const selectedBrand = formValues.brand ? brands.find(b => b._id === formValues.brand) : null;
                        
                        const debugInfo = {
                          formState: {
                            values: formValues,
                            isComplete: !!formValues.name && !!formValues.brand,
                          },
                          imageState: {
                            imageUrl,
                            hasImage: !!imageUrl,
                          },
                          modeInfo: {
                            isEditMode,
                            id: id || null,
                          },
                          brandInfo: selectedBrand ? {
                            id: selectedBrand._id,
                            name: selectedBrand.name || selectedBrand.brand_name,
                            isValid: /^[0-9a-fA-F]{24}$/.test(selectedBrand._id),
                          } : 'No brand selected',
                          apiEndpoints: {
                            collection: '/collections',
                            uploadImage: '/product/image/temp',
                            mediaUpload: '/media/upload',
                            productCreateTemp: '/product/create',
                            imageOptimization: '/product/imageoptimization',
                            directProductImage: '/product/image/:id'
                          },
                          environment: {
                            baseUrl: process.env.REACT_APP_API_URL || 'https://flooringdeals.ca/api',
                            nodeEnv: process.env.NODE_ENV,
                          }
                        };
                        
                        // Show a more condensed view in the message
                        message.info(
                          `Diagnostics logged to console. Form filled: ${debugInfo.formState.isComplete ? '✅' : '❌'}, ` +
                          `Has image: ${debugInfo.imageState.hasImage ? '✅' : '❌'}, ` +
                          `Edit mode: ${debugInfo.modeInfo.isEditMode ? '✅' : '❌'}, ` + 
                          `Brand valid: ${selectedBrand && debugInfo.brandInfo.isValid ? '✅' : '❌'}`
                        );
                        
                        console.log('🔍 Collection Debug Info:', debugInfo);
                      }}
                    >
                      Log Diagnostics
                    </Button>
                    
                    {/* Add a button to manually update the collection with the current image URL */}
                    {isEditMode && id && imageUrl && (
                      <Button
                        size="small"
                        className="mt-1"
                        danger
                        onClick={async () => {
                          try {
                            message.loading('Manually updating collection image...');
                            await CollectionService.updateCollection(id, { image: imageUrl });
                            message.success('Collection image manually updated');
                          } catch (error) {
                            message.error('Failed to update collection: ' + error.message);
                          }
                        }}
                      >
                        Force Update Collection Image
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
                style={{ backgroundColor: "#e67929", borderRadius: "10px", border: 'none' }}
              >
                {isEditMode ? 'Update Collection' : 'Create Collection'}
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Card>
    </div>
  );
};

export default CollectionForm;
